/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';
import { Trans } from 'react-i18next';

import { ReceptionServiceSimpleQuery } from '@dreryk/egabinet-private-medical-service-frontend-api';
import { ServiceLocationId, ServiceLocationType } from '@dreryk/egabinet-healthcare-provider-published-language-api';

import { ModalCloseButton, ModalConfirmButton, ModalWidth, ShowModalEventInterceptor } from '@dreryk/react-components';
import { resolveDeclensionTranslationKey, useTranslate } from '@dreryk/react-common';
import { performCqrsQueryCount } from '@dreryk/command-query-react-common';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';

import { serviceProvidingLocationTranslationKeys } from '../../../../../translations';

type ReceptionServiceAssignmentCheckEventInterceptorProps = {
   serviceLocationId: number;
   children: JSX.Element;
};

export const ReceptionServiceAssignmentCheckEventInterceptor = (
   props: ReceptionServiceAssignmentCheckEventInterceptorProps
): JSX.Element => {
   const translate = useTranslate();

   const shouldShowModal = useCallback(async () => {
      const searchingPrivateMedicalServicesAvailable = protectFeature('searchingPrivateMedicalServices');
      if (!searchingPrivateMedicalServicesAvailable) {
         return false;
      }

      const receptionsServicesCount = await performCqrsQueryCount(() => {
         const query = new ReceptionServiceSimpleQuery();
         query.serviceLocationId = Slot.of(
            ServiceLocationId.of(ServiceLocationType.SERVICE_PROVIDING_LOCATION, props.serviceLocationId)
         );
         return query;
      });

      if (receptionsServicesCount === 0) {
         return false;
      }

      return {
         receptionsServicesCount: receptionsServicesCount,
      };
   }, [props.serviceLocationId]);

   return (
      <ShowModalEventInterceptor
         shouldShowModal={shouldShowModal}
         testId={'receptionServiceAssignment'}
         title={serviceProvidingLocationTranslationKeys.cannotRemoveServiceProvidingLocation}
         footerItemsHidden
         modalWidth={ModalWidth.NORMAL}
         content={({ reject, result: { receptionsServicesCount } }) => {
            return (
               <>
                  <Trans
                     i18nKey={
                        serviceProvidingLocationTranslationKeys.cannotRemoveServiceProvidingLocationWithAssignedReceptionServices
                     }
                     values={{
                        count: receptionsServicesCount,
                        declensionForm: translate(
                           resolveDeclensionTranslationKey(
                              receptionsServicesCount,
                              serviceProvidingLocationTranslationKeys
                                 .cannotRemoveServiceProvidingLocationWithAssignedReceptionServicesDeclensionMap
                                 .accusative,
                              serviceProvidingLocationTranslationKeys
                                 .cannotRemoveServiceProvidingLocationWithAssignedReceptionServicesDeclensionMap
                                 .genitive,
                              serviceProvidingLocationTranslationKeys
                                 .cannotRemoveServiceProvidingLocationWithAssignedReceptionServicesDeclensionMap.plural
                           )
                        ),
                     }}
                  />
                  <ModalConfirmButton label={serviceProvidingLocationTranslationKeys.close} onClick={reject} />
                  <ModalCloseButton onClick={reject} />
               </>
            );
         }}
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
